import { useCallback, useEffect, useState } from "react";

const STORAGE_PREFIX = "selectra_projects_";

function readStore(key, seed) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return seed;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : seed;
  } catch {
    return seed;
  }
}

function writeStore(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage unavailable (private browsing, quota, etc.) — fail silently,
    // the in-memory state still works for the current session.
  }
}

/**
 * useLocalProjects
 * Per-location project list backed by browser localStorage, seeded from
 * siteData.js on first visit. Edits made through the returned actions
 * persist only in the browser/device they were made on — there is no
 * backend, so nothing here syncs to other visitors. Each location gets
 * its own storage key so the two offices don't share a list.
 */
export default function useLocalProjects(locationId, seedProjects) {
  const storageKey = `${STORAGE_PREFIX}${locationId}`;
  const [projects, setProjects] = useState(() => readStore(storageKey, seedProjects));

  useEffect(() => {
    setProjects(readStore(storageKey, seedProjects));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const persist = useCallback(
    (next) => {
      setProjects(next);
      writeStore(storageKey, next);
    },
    [storageKey]
  );

  const addProject = useCallback(
    (project) => {
      const withId = { ...project, id: project.id || `proj-${Date.now()}` };
      persist([withId, ...projects]);
    },
    [projects, persist]
  );

  const updateProject = useCallback(
    (id, updates) => {
      persist(projects.map((p) => (p.id === id ? { ...p, ...updates } : p)));
    },
    [projects, persist]
  );

  const deleteProject = useCallback(
    (id) => {
      persist(projects.filter((p) => p.id !== id));
    },
    [projects, persist]
  );

  const resetToDefault = useCallback(() => {
    persist(seedProjects);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persist]);

  const isCustomized = JSON.stringify(projects) !== JSON.stringify(seedProjects);

  return { projects, addProject, updateProject, deleteProject, resetToDefault, isCustomized };
}
